import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, BookOpen, Star, Sparkles, Heart, Mountain, Sun, ExternalLink } from 'lucide-react';
import { SeoMeta } from '../components/seo/SeoMeta';

const BlogPost2: React.FC = () => {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [surname, setSurname] = useState('');

  const handleGenerateMeaningfulName = (e: React.FormEvent) => {
    e.preventDefault();
    if (firstName.trim() && surname.trim()) {
      navigate('/chinese-name-generator', { 
        state: { 
          firstName: firstName.trim(),
          surname: surname.trim(),
          autoGenerate: true 
        } 
      });
    }
  };
  const popularNames = [
    { chinese: '李明', pinyin: 'Lǐ Míng', meaning: 'Bright/Intelligent', category: 'Traditional' },
    { chinese: '王雅', pinyin: 'Wáng Yǎ', meaning: 'Elegant/Graceful', category: 'Modern' },
    { chinese: '张伟', pinyin: 'Zhāng Wěi', meaning: 'Great/Magnificent', category: 'Popular' },
    { chinese: '刘欣', pinyin: 'Liú Xīn', meaning: 'Joyful/Happy', category: 'Contemporary' }
  ];

  return (
    <>
      <SeoMeta
        title="Chinese Names and Meanings: Understanding the Cultural Significance"
        description="Ever wonder what Chinese names really mean? Discover how each character carries profound meaning - from virtues like 德 (virtue) to nature elements like 山 (mountain). Learn how Chinese parents choose characters that express hopes for their children's future. Explore popular names with beautiful meanings, understand cultural symbolism, and find names that match your values. Complete guide with character breakdowns and cultural context."
        keywords="chinese names and meanings, chinese name meaning dictionary, what do chinese names mean, beautiful chinese names and meanings, chinese names with good meaning, popular chinese names and meanings, chinese character name meanings, traditional chinese names meaning, chinese names that mean strength, chinese names meaning lucky"
        pathname="/blog/chinese-names-meanings"
      />

      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-orange-50">
        {/* Navigation */}
        <nav className="bg-white shadow-sm border-b">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <Link 
              to="/blog"
              className="inline-flex items-center gap-2 text-orange-600 hover:text-orange-700 font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
          </div>
        </nav>

        {/* Article Header */}
        <header className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              Cultural Insights
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Chinese Names and Their Meanings: A Cultural Deep Dive
            </h1>
            
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Discover the deep meanings behind Chinese names and their cultural significance. Learn how Chinese characters create meaningful names and explore popular Chinese names with beautiful meanings.
            </p>

            <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 mb-8">
              <span>Published: June 12, 2025</span>
              <span>10 min read</span>
              <span>Cultural Deep Dive</span>
            </div>

            {/* Interactive Meaningful Name Generator */}
            <div className="bg-gradient-to-r from-orange-600 to-red-600 rounded-xl p-6 text-white mb-12">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Create Names with Meaningful Significance
              </h3>
              <form onSubmit={handleGenerateMeaningfulName} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">First Name</label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg bg-white/90 text-gray-900 placeholder-gray-500"
                      placeholder="Your first name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Last Name</label>
                    <input
                      type="text"
                      value={surname}
                      onChange={(e) => setSurname(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg bg-white/90 text-gray-900 placeholder-gray-500"
                      placeholder="Your last name"
                      required
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={!firstName.trim() || !surname.trim()}
                  className="bg-white text-orange-600 px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all flex items-center gap-2 disabled:bg-gray-200 disabled:text-gray-400"
                >
                  Generate Meaningful Name
                  <ArrowRight className="w-4 h-4" />
                </button>
              </form>
            </div>
          </div>
        </header>

        {/* Article Content */}
        <article className="px-4 pb-16">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              
              {/* Table of Contents */}
              <div className="bg-orange-50 rounded-xl p-6 mb-8">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Star className="w-5 h-5 text-orange-600" />
                  What You'll Discover
                </h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• The art of Chinese name meanings and symbolism</li>
                  <li>• Most popular Chinese names and their significance</li>
                  <li>• How Chinese characters create meaningful combinations</li>
                  <li>• Traditional vs modern Chinese name meanings</li>
                </ul>
              </div>

              {/* Section 1 */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <div className="w-8 h-8 bg-orange-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">1</div>
                  The Art of Chinese Name Meanings
                </h2>
                
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Understanding <strong>chinese names and meanings</strong> requires appreciating the rich symbolism embedded in Chinese characters. Unlike many Western naming traditions, every Chinese character carries specific meanings, and their combinations create layered significance.
                </p>

                <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-6">
                  <h4 className="font-bold text-blue-800 mb-3 flex items-center gap-2">
                    <BookOpen className="w-5 h-5" />
                    Fundamental Principles
                  </h4>
                  <ul className="text-blue-700 space-y-2">
                    <li>• Each character has both literal and symbolic meanings</li>
                    <li>• Character combinations create unique significance</li>
                    <li>• Sound, tone, and visual aesthetics matter</li>
                    <li>• Cultural and historical context influences interpretation</li>
                  </ul>
                </div>

                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  When exploring <strong>what do chinese names mean</strong>, you'll discover that names often reflect parents' aspirations, cultural values, and hopes for their children's future. This makes Chinese names profoundly personal and meaningful.
                </p>

                {/* Popular Names Showcase */}
                <div className="grid md:grid-cols-2 gap-4 mb-8">
                  {popularNames.map((name, index) => (
                    <div key={index} className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl p-4 border border-orange-200">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="text-xl font-bold text-gray-900">{name.chinese}</h4>
                        <span className="text-xs bg-orange-200 text-orange-800 px-2 py-1 rounded-full">{name.category}</span>
                      </div>
                      <p className="text-orange-600 font-medium mb-1">{name.pinyin}</p>
                      <p className="text-gray-700 text-sm">{name.meaning}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Section 2 */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <div className="w-8 h-8 bg-orange-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">2</div>
                  Most Popular Chinese Names and Their Significance
                </h2>

                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Exploring <strong>popular chinese names and meanings</strong> reveals fascinating patterns in Chinese culture. Here are the most beloved categories and their significance:
                </p>

                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-blue-50 rounded-xl p-6 text-center">
                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Mountain className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="font-bold text-blue-800 mb-3">Nature-Inspired Names</h4>
                    <ul className="text-blue-700 text-sm space-y-1">
                      <li>山 (Shān) - Mountain</li>
                      <li>海 (Hǎi) - Ocean</li>
                      <li>林 (Lín) - Forest</li>
                      <li>花 (Huā) - Flower</li>
                    </ul>
                  </div>

                  <div className="bg-purple-50 rounded-xl p-6 text-center">
                    <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Heart className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="font-bold text-purple-800 mb-3">Virtue-Based Names</h4>
                    <ul className="text-purple-700 text-sm space-y-1">
                      <li>德 (Dé) - Virtue</li>
                      <li>仁 (Rén) - Benevolence</li>
                      <li>智 (Zhì) - Wisdom</li>
                      <li>勇 (Yǒng) - Courage</li>
                    </ul>
                  </div>

                  <div className="bg-yellow-50 rounded-xl p-6 text-center">
                    <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Sun className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="font-bold text-yellow-800 mb-3">Success & Prosperity</h4>
                    <ul className="text-yellow-700 text-sm space-y-1">
                      <li>成 (Chéng) - Success</li>
                      <li>富 (Fù) - Wealth</li>
                      <li>贵 (Guì) - Noble</li>
                      <li>强 (Qiáng) - Strong</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-green-50 border-l-4 border-green-500 p-6 mb-6">
                  <h4 className="font-bold text-green-800 mb-2">Beautiful Chinese Names and Meanings</h4>
                  <p className="text-green-700 mb-3">
                    The most <strong>beautiful chinese names and meanings</strong> often combine poetic imagery with positive aspirations:
                  </p>
                  <ul className="text-green-700 space-y-1">
                    <li>• 梦琪 (Mèng Qí) - "Dream of Precious Jade"</li>
                    <li>• 思雅 (Sī Yǎ) - "Thoughtful Elegance"</li>
                    <li>• 浩然 (Hào Rán) - "Vast and Natural"</li>
                    <li>• 心怡 (Xīn Yí) - "Heart's Joy"</li>
                  </ul>
                </div>

                {/* Internal Link CTA */}
                <div className="bg-purple-50 rounded-xl p-6 mb-8">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-bold text-purple-800 mb-2">Generate Meaningful Names</h4>
                      <p className="text-purple-700">Create Chinese names with detailed meaning explanations</p>
                    </div>
                    <Link 
                      to="/chinese-name-generator"
                      className="bg-purple-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center gap-2"
                    >
                      Try Generator
                      <ExternalLink className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </section>

              {/* Section 3 */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <div className="w-8 h-8 bg-orange-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">3</div>
                  How Chinese Characters Create Meaningful Names
                </h2>

                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Understanding <strong>chinese character name meanings</strong> is key to appreciating the depth of Chinese nomenclature. Characters can be combined in various ways to create unique significance.
                </p>

                <div className="bg-orange-50 rounded-xl p-6 mb-6">
                  <h4 className="font-bold text-orange-800 mb-3">Character Combination Methods</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <h5 className="font-semibold text-orange-700 mb-2">Complementary Meanings</h5>
                      <p className="text-orange-600 text-sm">两个字互相补充，创造更丰富的含义</p>
                      <p className="text-gray-600 text-sm">Example: 文武 (Wén Wǔ) - "Literature and Martial Arts"</p>
                    </div>
                    <div>
                      <h5 className="font-semibold text-orange-700 mb-2">Reinforcing Concepts</h5>
                      <p className="text-orange-600 text-sm">两个字强化同一个概念</p>
                      <p className="text-gray-600 text-sm">Example: 明亮 (Míng Liàng) - "Bright and Luminous"</p>
                    </div>
                  </div>
                </div>

                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Many people wonder about <strong>chinese names that mean strength</strong> or <strong>chinese names meaning lucky</strong>. These concepts are beautifully expressed through specific character combinations that have been refined over thousands of years.
                </p>

                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-red-50 rounded-xl p-6">
                    <h4 className="font-bold text-red-800 mb-3">Strength-Related Characters</h4>
                    <ul className="text-red-700 space-y-2">
                      <li>• 强 (Qiáng) - Strong, Powerful</li>
                      <li>• 刚 (Gāng) - Firm, Unyielding</li>
                      <li>• 威 (Wēi) - Mighty, Majestic</li>
                      <li>• 勇 (Yǒng) - Brave, Courageous</li>
                    </ul>
                  </div>

                  <div className="bg-green-50 rounded-xl p-6">
                    <h4 className="font-bold text-green-800 mb-3">Lucky/Fortune Characters</h4>
                    <ul className="text-green-700 space-y-2">
                      <li>• 福 (Fú) - Fortune, Blessing</li>
                      <li>• 吉 (Jí) - Lucky, Auspicious</li>
                      <li>• 瑞 (Ruì) - Auspicious, Propitious</li>
                      <li>• 祥 (Xiáng) - Good Fortune</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Section 4 */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <div className="w-8 h-8 bg-orange-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">4</div>
                  Traditional vs Modern Chinese Name Meanings
                </h2>

                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  The evolution from <strong>traditional chinese names meaning</strong> to contemporary choices reflects changing cultural values while maintaining core symbolic principles.
                </p>

                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  <div className="bg-amber-50 rounded-xl p-6">
                    <h4 className="font-bold text-amber-800 mb-4">Traditional Names (传统名字)</h4>
                    <ul className="text-amber-700 space-y-3">
                      <li>
                        <strong>家族传承</strong> - Family lineage focus
                        <p className="text-sm text-amber-600">Names reflecting family honor and ancestral values</p>
                      </li>
                      <li>
                        <strong>五行平衡</strong> - Five elements balance
                        <p className="text-sm text-amber-600">Names chosen to balance metal, wood, water, fire, earth</p>
                      </li>
                      <li>
                        <strong>儒家理想</strong> - Confucian ideals
                        <p className="text-sm text-amber-600">Emphasizing virtue, learning, and social harmony</p>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-blue-50 rounded-xl p-6">
                    <h4 className="font-bold text-blue-800 mb-4">Modern Names (现代名字)</h4>
                    <ul className="text-blue-700 space-y-3">
                      <li>
                        <strong>个人表达</strong> - Personal expression
                        <p className="text-sm text-blue-600">Names reflecting individual personality and dreams</p>
                      </li>
                      <li>
                        <strong>国际视野</strong> - Global perspective
                        <p className="text-sm text-blue-600">Names that work across cultures and languages</p>
                      </li>
                      <li>
                        <strong>创新组合</strong> - Creative combinations
                        <p className="text-sm text-blue-600">Fresh character pairings with contemporary appeal</p>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Cultural Insights Link */}
                <div className="bg-indigo-50 rounded-xl p-6 mb-8">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-bold text-indigo-800 mb-2">Deep Dive into Chinese Culture</h4>
                      <p className="text-indigo-700">Explore the rich traditions behind Chinese naming practices</p>
                    </div>
                    <Link 
                      to="/cultural-insights"
                      className="bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-indigo-700 transition-colors flex items-center gap-2"
                    >
                      Learn More
                      <ExternalLink className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </section>

              {/* Conclusion CTA */}
              <section className="mb-8">
                <div className="bg-gradient-to-r from-orange-600 to-pink-600 rounded-2xl p-8 text-white text-center">
                  <h3 className="text-2xl font-bold mb-4">Discover Your Name's Deep Meaning</h3>
                  <p className="text-lg opacity-90 mb-6">
                    Generate Chinese names with detailed explanations of their cultural significance and symbolic meanings.
                  </p>
                  <Link 
                    to="/chinese-name-generator"
                    className="inline-flex items-center gap-2 bg-white text-orange-600 px-8 py-4 rounded-xl font-bold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                  >
                    Explore Name Meanings
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
              </section>
            </div>
          </div>
        </article>

        {/* Navigation Footer */}
        <footer className="py-12 px-4 bg-white border-t">
          <div className="max-w-4xl mx-auto">
            <div className="flex justify-between items-center">
              <Link 
                to="/blog/how-to-choose-chinese-name"
                className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
                Previous: How to Choose a Chinese Name
              </Link>
              
              <Link 
                to="/blog/chinese-names-pronunciation"
                className="inline-flex items-center gap-2 text-orange-600 hover:text-orange-700 font-medium"
              >
                Next: Chinese Names Pronunciation
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default BlogPost2; 